"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface FocusModeContextType {
  sidebarCollapsed: boolean;
  headerHidden: boolean;
  toggleSidebar: () => void;
  enterFocusMode: () => void;
  exitFocusMode: () => void;
}

const FocusModeContext = createContext<FocusModeContextType>({
  sidebarCollapsed: false,
  headerHidden: false,
  toggleSidebar: () => {},
  enterFocusMode: () => {},
  exitFocusMode: () => {},
});

export function FocusModeProvider({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const preFocusState = useRef<boolean | null>(null);

  // Initialize from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("sf_sidebar_collapsed");
    if (stored === "true") setSidebarCollapsed(true);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sf_sidebar_collapsed", String(next));
      return next;
    });
  }, []);

  const enterFocusMode = useCallback(() => {
    setSidebarCollapsed((prev) => {
      if (preFocusState.current === null) {
        preFocusState.current = prev;
      }
      return true;
    });
    setHeaderHidden(true);
  }, []);

  const exitFocusMode = useCallback(() => {
    if (preFocusState.current !== null) {
      const restore = preFocusState.current;
      preFocusState.current = null;
      setSidebarCollapsed(restore);
      localStorage.setItem("sf_sidebar_collapsed", String(restore));
    }
    setHeaderHidden(false);
  }, []);

  return (
    <FocusModeContext.Provider value={{ sidebarCollapsed, headerHidden, toggleSidebar, enterFocusMode, exitFocusMode }}>
      {children}
    </FocusModeContext.Provider>
  );
}

export function useFocusMode() {
  return useContext(FocusModeContext);
}
